// src/components/admin/mail/MailAttachmentList.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { Download, Loader2, Paperclip } from 'lucide-react';
import { MailAttachment, MailFolderId } from '../../../types/mail';
import { downloadAttachment, readBlobError } from './mailApi';
import { formatBytes } from './mailUtils';
import { BORDER, MUTED, SURFACE } from './mail.styles';

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 0.6rem;
    flex-wrap: wrap;
    padding: 0.85rem 1.25rem;
    border-top: 1px solid ${BORDER};
    background: ${SURFACE};
    box-sizing: border-box;
`;

const Label = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: ${MUTED};
`;

const Chip = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    max-width: 100%;
    padding: 0.5rem 0.8rem;
    border: 1.5px solid ${BORDER};
    background: #ffffff;
    border-radius: 8px;
    font-size: 0.82rem;
    color: #334155;
    cursor: pointer;
    transition: all 0.2s ease;
    box-sizing: border-box;

    &:hover:not(:disabled) {
        border-color: #990147;
        color: #990147;
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.6;
        cursor: progress;
        pointer-events: none;
    }

    &:focus-visible {
        outline: 3px solid rgba(153, 1, 71, 0.3);
        outline-offset: 2px;
    }
`;

const Name = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 220px;
`;

const Size = styled.span`
    color: #94a3b8;
    font-size: 0.75rem;
    flex-shrink: 0;
`;

const Spinning = styled(Loader2)`
    animation: spin 0.9s linear infinite;
    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`;

interface Props {
    accountId: string;
    folder: MailFolderId;
    uid: number;
    uidValidity: number;
    attachments: MailAttachment[];
}

const MailAttachmentList: React.FC<Props> = ({ accountId, folder, uid, uidValidity, attachments }) => {
    // Per-attachment, not global: she may well want two files, and disabling all of them
    // while one downloads would be needlessly obstructive.
    const [downloading, setDownloading] = useState<string | null>(null);

    if (!attachments || attachments.length === 0) return null;

    const handleDownload = async (attachment: MailAttachment) => {
        setDownloading(attachment.partId);
        try {
            await downloadAttachment(accountId, folder, uid, uidValidity, attachment);
        } catch (err: any) {
            const serverMessage = await readBlobError(err);
            toast.error(serverMessage || 'Prílohu sa nepodarilo stiahnuť');
        } finally {
            setDownloading(null);
        }
    };

    return (
        <Wrapper>
            <Label>
                <Paperclip size={14} />
                {attachments.length === 1 ? '1 príloha' : `${attachments.length} prílohy`}
            </Label>
            {attachments.map((attachment) => (
                <Chip
                    key={attachment.partId}
                    type="button"
                    onClick={() => handleDownload(attachment)}
                    disabled={downloading === attachment.partId}
                    title={`Stiahnuť ${attachment.fileName}`}
                >
                    {downloading === attachment.partId ? <Spinning size={14} /> : <Download size={14} />}
                    <Name>{attachment.fileName}</Name>
                    {attachment.sizeBytes > 0 && <Size>{formatBytes(attachment.sizeBytes)}</Size>}
                </Chip>
            ))}
        </Wrapper>
    );
};

export default MailAttachmentList;
